import React, { useEffect } from "react";
import { createPortal } from "react-dom";

function formatBatchDate(value) {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const ShelfLifeBatchConfirmPopUp = ({
  open = false,
  onClose,
  onProceed,
  loading = false,
  impact = null,
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose, loading]);

  if (!open) return null;

  const currentShelfLife = impact?.currentShelfLifeDays ?? "—";
  const newShelfLife = impact?.newShelfLifeDays ?? "—";
  const affectedBatches = Array.isArray(impact?.affectedBatches)
    ? impact.affectedBatches
    : [];

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shelf-life-batch-confirm-title"
    >
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(16,24,40,0.35)" }}
        onClick={() => {
          if (!loading) onClose?.();
        }}
        aria-hidden="true"
      />
      <div
        className="relative w-full bg-white overflow-hidden"
        style={{
          width: 760,
          maxWidth: "94vw",
          maxHeight: "88vh",
          borderRadius: 18,
          padding: "22px 22px 18px",
          border: "1px solid #EEF1F5",
          boxShadow: "0 12px 30px rgba(16,24,40,0.14)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            if (!loading) onClose?.();
          }}
          className="absolute flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
          style={{ top: 16, right: 16, color: "#98A2B3" }}
          aria-label="Close"
          disabled={loading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="pr-8">
          <h2
            id="shelf-life-batch-confirm-title"
            style={{ fontSize: 22, fontWeight: 600, color: "#101828", marginTop: 10, marginBottom: 0 }}
          >
            Confirm Shelf Life Change
          </h2>
          <p style={{ fontSize: 14, color: "#667085", lineHeight: 1.6, marginTop: 8, marginBottom: 0 }}>
            Changing Shelf Life from {currentShelfLife} days to {newShelfLife} days will recalculate the
            expiry dates of the following batches.
          </p>
        </div>

        <div
          className="mt-4 overflow-auto"
          style={{ maxHeight: "calc(88vh - 220px)" }}
        >
          <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#F6F7FA" }}>
                <th className="text-left font-semibold text-[#101828] px-3 py-2">Batch</th>
                <th className="text-left font-semibold text-[#101828] px-3 py-2">Manufacturing Date</th>
                <th className="text-left font-semibold text-[#101828] px-3 py-2">Current Expiry</th>
                <th className="text-left font-semibold text-[#101828] px-3 py-2">New Expiry</th>
              </tr>
            </thead>
            <tbody>
              {affectedBatches.map((batch) => (
                <tr key={batch.batchId || batch.batchNumber} style={{ borderTop: "1px solid #EEF1F5" }}>
                  <td className="px-3 py-2 text-[#101828]">{batch.batchNumber || "—"}</td>
                  <td className="px-3 py-2 text-[#344054]">{formatBatchDate(batch.manufacturingDate)}</td>
                  <td className="px-3 py-2 text-[#344054]">{formatBatchDate(batch.currentExpiryDate)}</td>
                  <td className="px-3 py-2 text-[#101828] font-medium">{formatBatchDate(batch.newExpiryDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex gap-4 mt-[18px]">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 rounded-[10px] border font-semibold transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              height: 46,
              backgroundColor: "#FFFFFF",
              borderColor: "#D0D5DD",
              color: "#101828",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onProceed}
            disabled={loading}
            className="flex-1 rounded-[10px] border-0 font-semibold text-white transition hover:bg-[#14753A] focus:outline-none focus:ring-2 focus:ring-green-200 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ height: 46, backgroundColor: "#188742" }}
          >
            {loading ? "Updating..." : "Proceed"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default ShelfLifeBatchConfirmPopUp;
